// Slice-internal Konva renderer for checkboxes (TR4).
//
// Extracted 1:1 from `objects/board-object-renderer.ts`
// (createCheckboxNode / updateCheckboxNode branch). The checkbox slice
// produces a `Konva.Group` containing a `Konva.Rect` body and conditionally
// a `Konva.Line` check mark when `checked: true`.

import Konva from 'konva';
import type { SliceRenderContext } from '../../core/object-registry.js';
import type { CheckboxObject } from './checkbox.interface.js';
import { DEFAULT_CHECKBOX_ICON } from './checkbox-icons.config.js';

export const CHECKBOX_NAME = 'checkbox-body';
export const CHECKBOX_CHECK_NAME = 'checkbox-check';

/**
 * Minimum stroke width, expressed in SCREEN pixels (not world units) —
 * Stefan, 2026-08-28: border thickness and corner radius must scale along
 * with the now zoom-coupled checkbox size (`checkbox-defaults.factory.ts`),
 * not stay pinned to an absolute world-unit floor.
 *
 * Konva draws `strokeWidth`/`cornerRadius` in the SAME world-space as
 * `width`/`height`, which the stage then multiplies by the current zoom —
 * so a `Math.max(2, ...)` floor in world units becomes a wildly different
 * ON-SCREEN thickness depending on zoom (e.g. 20px at 1000% zoom, 0.2px at
 * 10% zoom), even though `obj.width * 0.06` alone stays proportional and
 * would already look right on screen. Converting the floor itself to world
 * units via `/ scale` keeps it at a constant ~2 screen px regardless of
 * zoom, matching how `obj.width` was already made to look ~constant on
 * screen (`checkboxSizesForScale`).
 */
const MIN_STROKE_SCREEN_PX = 2;

function strokeWidthFor(obj: CheckboxObject, scale: number): number {
  return Math.max(MIN_STROKE_SCREEN_PX / scale, obj.width * 0.06);
}

/** Build a fresh Konva.Group rendering the checkbox. */
export function createNode(obj: CheckboxObject, ctx: SliceRenderContext): Konva.Group {
  const group = new Konva.Group({
    id: obj.id, x: obj.x, y: obj.y,
    width: obj.width, height: obj.height,
    rotation: obj.rotation, name: 'checkbox-obj',
  });

  const scale = ctx.viewport.scale || 1;
  const strokeW = strokeWidthFor(obj, scale);
  // Proportional to width (0.1), same ratio `r = 4` originally had against
  // the old fixed `DEFAULT_SIZE = 40` (4 / 40 = 0.1) — reproduces the exact
  // original look at that reference size, now scaling with obj.width like
  // everything else instead of staying a flat 4 world units regardless of
  // (now zoom-dependent) size.
  const r = obj.width * 0.1;
  const box = new Konva.Rect({
    name: CHECKBOX_NAME,
    x: 0, y: 0,
    width: obj.width, height: obj.height,
    stroke: obj.color,
    strokeWidth: strokeW,
    fill: obj.checked ? obj.color : '',
    cornerRadius: r,
    listening: true,
  });
  group.add(box);

  if (obj.checked) {
    group.add(buildIcon(obj, scale));
  }
  return group;
}

/** Apply changed properties from an updated CheckboxObject onto its node. */
export function updateNode(node: Konva.Node, obj: CheckboxObject, ctx: SliceRenderContext): void {
  if (!(node instanceof Konva.Group)) return;
  const scale = ctx.viewport.scale || 1;
  const strokeW = strokeWidthFor(obj, scale);
  const r = obj.width * 0.1;
  const box = node.findOne<Konva.Rect>(`.${CHECKBOX_NAME}`);
  if (box) {
    box.setAttrs({
      width: obj.width, height: obj.height,
      stroke: obj.color, strokeWidth: strokeW,
      fill: obj.checked ? obj.color : '',
      cornerRadius: r,
    });
  }
  node.findOne(`.${CHECKBOX_CHECK_NAME}`)?.destroy();
  if (obj.checked) {
    node.add(buildIcon(obj, scale));
  }
}

/**
 * Build the white glyph shown when `checked: true`. Which shape depends on
 * `obj.icon` (Stefan, 2026-08-29 — three choices wired via part9.svg's
 * options-circle buttons, see `checkbox-icons.config.ts`).
 *
 * The 'check' case stays a bare `Konva.Line` (same node shape as before this
 * feature — kept for the existing `findOne<Konva.Line>('.checkbox-check')`
 * call sites/tests). 'cross' and 'smiley' need more than one primitive, so
 * they come back as a `Konva.Group` carrying the same name — `findOne`
 * matches either shape.
 *
 * Point fractions for all three are lifted directly from part9.svg's
 * `action-checkbox-icon-1/2/3` groups, normalised against that button's
 * 14.53×14.53 box (e.g. the check mark's `polyline points="114.43 135.65
 * 117.8 138.84 123.66 132.41"` inside a rect at `x=111.65 y=127.61`).
 * (Stefan, 2026-08-29 — updated after the "alle neu"/"nochmal neu" part9
 * refreshes shifted these coordinates slightly; the fractions below already
 * matched the newer asset, only this comment's example had gone stale.)
 */
function buildIcon(obj: CheckboxObject, scale: number): Konva.Group | Konva.Shape {
  const icon = obj.icon ?? DEFAULT_CHECKBOX_ICON;
  const w = obj.width;
  const h = obj.height;
  // Same screen-px-floor reasoning as `strokeWidthFor` above.
  const strokeW = Math.max(MIN_STROKE_SCREEN_PX / scale, obj.width * 0.1);

  if (icon === 'cross') {
    const group = new Konva.Group({ name: CHECKBOX_CHECK_NAME, listening: false });
    const lineOpts = { stroke: '#ffffff', strokeWidth: strokeW, lineCap: 'round' as const };
    group.add(new Konva.Line({ ...lineOpts, points: [w * 0.23, h * 0.23, w * 0.77, h * 0.77] }));
    group.add(new Konva.Line({ ...lineOpts, points: [w * 0.77, h * 0.23, w * 0.23, h * 0.77] }));
    return group;
  }

  if (icon === 'smiley') {
    const group = new Konva.Group({ name: CHECKBOX_CHECK_NAME, listening: false });
    const r = w * 0.0964;
    group.add(new Konva.Circle({ x: w * 0.353, y: h * 0.354, radius: r, fill: '#ffffff' }));
    group.add(new Konva.Circle({ x: w * 0.6545, y: h * 0.354, radius: r, fill: '#ffffff' }));
    group.add(new Konva.Line({
      points: [w * 0.264, h * 0.612, w * 0.615, h * 0.855, w * 0.734, h * 0.612],
      stroke: '#ffffff',
      strokeWidth: strokeW,
      lineCap: 'round',
      lineJoin: 'round',
      tension: 0.5,
    }));
    return group;
  }

  const p = w * 0.1913;
  return new Konva.Line({
    name: CHECKBOX_CHECK_NAME,
    points: [
      p,          h * 0.5534,
      w * 0.4225, h * 0.7728,
      w * 0.8258, h * 0.3303,
    ],
    stroke: '#ffffff',
    strokeWidth: strokeW,
    lineCap: 'round',
    lineJoin: 'round',
    listening: false,
  });
}
